// ─────────────────────────────────────────────────────────────────────────────
// Shared click-to-edit primitives for the offering / persona / sale detail
// pages. Modeled on InlineRenameTitle: display-styled at rest with a hover
// pencil, editable on click, Enter/blur saves, Escape cancels, spinner while
// saving. Each takes an async save callback. Text/list editors retain their
// draft after a failed save so transient auth or network errors are retryable;
// the caller's mutation supplies the visible error message.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Quote, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";

type SaveFn<T> = (value: T) => void | Promise<unknown>;

const DISPLAY_EMPTY = "italic text-muted-foreground/50 font-normal normal-case";

// ---------------------------------------------------------------------------
// InlineText — single-line text
// ---------------------------------------------------------------------------
export function InlineText({
  value,
  onSave,
  placeholder = "Add…",
  className = "",
  inputClassName = "",
  testId,
  maxLength = 500,
  disabled = false,
}: {
  value: string | null | undefined;
  onSave: SaveFn<string>;
  placeholder?: string;
  /** Display (rest-state) styling — matches the surrounding layout. */
  className?: string;
  inputClassName?: string;
  testId: string;
  maxLength?: number;
  disabled?: boolean;
}) {
  const current = (value ?? "").trim();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedDraft, setSavedDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards the blur handler after Enter/Escape already resolved the edit.
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (saving || disabled) return;
    resolvedRef.current = false;
    setDraft(savedDraft ?? current);
    setEditing(true);
  };

  const commit = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setEditing(false);
    const next = draft.trim().slice(0, maxLength);
    if (next === current) return;
    setSaving(true);
    setSavedDraft(next);
    try {
      await onSave(next);
      setSavedDraft(null);
    } catch {
      // Leave the failed value visible, but do not steal focus: another editor
      // may already be active and its blur-save must not start a retry loop.
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    resolvedRef.current = true;
    setEditing(false);
    setSavedDraft(null);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`w-full min-w-0 bg-background border border-input rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring ${className} ${inputClassName}`}
        data-testid={`input-${testId}`}
      />
    );
  }

  const shown = savedDraft ?? current;
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
      className={`group/ie inline-flex items-start gap-1.5 max-w-full text-left rounded-sm ${
        disabled ? "" : "cursor-text hover:bg-muted/40 transition-colors"
      } ${className}`}
      title={disabled ? undefined : "Click to edit"}
      data-testid={`text-${testId}`}
    >
      <span className={`min-w-0 ${shown ? "" : DISPLAY_EMPTY}`}>{shown || placeholder}</span>
      {saving ? (
        <Loader2 className="h-3 w-3 shrink-0 mt-1 animate-spin text-muted-foreground" />
      ) : (
        !disabled && (
          <Pencil className="h-3 w-3 shrink-0 mt-1 text-muted-foreground opacity-0 group-hover/ie:opacity-100 transition-opacity" />
        )
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// InlineTextarea — multi-line text (blur saves, Enter = newline, Esc cancels)
// ---------------------------------------------------------------------------
export function InlineTextarea({
  value,
  onSave,
  placeholder = "Add…",
  className = "",
  testId,
  maxLength = 8000,
  rows = 3,
  disabled = false,
}: {
  value: string | null | undefined;
  onSave: SaveFn<string>;
  placeholder?: string;
  className?: string;
  testId: string;
  maxLength?: number;
  rows?: number;
  disabled?: boolean;
}) {
  const current = (value ?? "").trim();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedDraft, setSavedDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (editing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
  }, [editing]);

  const startEdit = () => {
    if (saving || disabled) return;
    resolvedRef.current = false;
    setDraft(savedDraft ?? current);
    setEditing(true);
  };

  const commit = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setEditing(false);
    const next = draft.trim().slice(0, maxLength);
    if (next === current) return;
    setSaving(true);
    setSavedDraft(next);
    try {
      await onSave(next);
      setSavedDraft(null);
    } catch {
      // Keep the failed draft visible without taking focus from another field.
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    resolvedRef.current = true;
    setEditing(false);
    setSavedDraft(null);
  };

  if (editing) {
    const lineCount = draft.split("\n").length;
    return (
      <textarea
        ref={inputRef}
        value={draft}
        maxLength={maxLength}
        rows={Math.min(Math.max(rows, lineCount + 1), 14)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            commit();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`w-full bg-background border border-input rounded-md px-2 py-1.5 outline-none focus:ring-1 focus:ring-ring resize-y ${className}`}
        data-testid={`input-${testId}`}
      />
    );
  }

  const shown = savedDraft ?? current;
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
      className={`group/ie flex items-start gap-1.5 max-w-full text-left rounded-sm whitespace-pre-line ${
        disabled ? "" : "cursor-text hover:bg-muted/40 transition-colors"
      } ${className}`}
      title={disabled ? undefined : "Click to edit"}
      data-testid={`text-${testId}`}
    >
      <span className={`min-w-0 flex-1 ${shown ? "" : DISPLAY_EMPTY}`}>{shown || placeholder}</span>
      {saving ? (
        <Loader2 className="h-3 w-3 shrink-0 mt-1 animate-spin text-muted-foreground" />
      ) : (
        !disabled && (
          <Pencil className="h-3 w-3 shrink-0 mt-1 text-muted-foreground opacity-0 group-hover/ie:opacity-100 transition-opacity" />
        )
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// InlineSelect — dropdown-backed field styled like display content
// ---------------------------------------------------------------------------
export function InlineSelect({
  value,
  options,
  onSave,
  placeholder = "Select…",
  className = "",
  testId,
  disabled = false,
}: {
  value: string | null | undefined;
  options: { value: string; label: string }[];
  onSave: SaveFn<string>;
  placeholder?: string;
  /** Applied to the trigger — style like the display text it replaces. */
  className?: string;
  testId: string;
  disabled?: boolean;
}) {
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  const handle = async (v: string) => {
    if (v === value) return;
    setSaving(true);
    setPending(v);
    try {
      await onSave(v);
    } catch {
      // revert via props
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  const shown = pending ?? ((value ?? "").trim() || null);
  const label = shown ? options.find((o) => o.value === shown)?.label ?? shown.replace(/_/g, " ") : "";

  return (
    <Select value={shown ?? undefined} onValueChange={handle} disabled={saving || disabled}>
      <SelectTrigger
        className={`h-auto w-auto min-w-0 justify-start gap-1 border-0 bg-transparent p-0 shadow-none rounded-sm hover:bg-muted/40 focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-40 ${className}`}
        data-testid={`select-${testId}`}
      >
        <span className={`truncate ${label ? "" : DISPLAY_EMPTY}`}>{label || placeholder}</span>
        {saving && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />}
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// InlineDate — native date input behind a display span
// ---------------------------------------------------------------------------
export function InlineDate({
  value,
  onSave,
  display,
  placeholder = "Add date…",
  className = "",
  testId,
  disabled = false,
}: {
  /** ISO datetime string (or null). */
  value: string | null | undefined;
  /** Receives an ISO string, or null when cleared. */
  onSave: SaveFn<string | null>;
  /** Formatted display text (falls back to a locale date). */
  display?: string | null;
  placeholder?: string;
  className?: string;
  testId: string;
  disabled?: boolean;
}) {
  const currentYmd = value ? String(value).slice(0, 10) : "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    if (saving || disabled) return;
    resolvedRef.current = false;
    setDraft(currentYmd);
    setEditing(true);
  };

  const commit = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setEditing(false);
    if (draft === currentYmd) return;
    setSaving(true);
    try {
      await onSave(draft ? new Date(`${draft}T00:00:00.000Z`).toISOString() : null);
    } catch {
      // revert via props
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    resolvedRef.current = true;
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`bg-background border border-input rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring ${className}`}
        data-testid={`input-${testId}`}
      />
    );
  }

  const shownText =
    (display ?? "").trim()
    || (value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
      className={`group/ie inline-flex items-start gap-1.5 max-w-full text-left rounded-sm ${
        disabled ? "" : "cursor-pointer hover:bg-muted/40 transition-colors"
      } ${className}`}
      title={disabled ? undefined : "Click to edit"}
      data-testid={`text-${testId}`}
    >
      <span className={`min-w-0 ${shownText ? "" : DISPLAY_EMPTY}`}>{shownText || placeholder}</span>
      {saving ? (
        <Loader2 className="h-3 w-3 shrink-0 mt-1 animate-spin text-muted-foreground" />
      ) : (
        !disabled && (
          <Pencil className="h-3 w-3 shrink-0 mt-1 text-muted-foreground opacity-0 group-hover/ie:opacity-100 transition-opacity" />
        )
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// InlineStringList — add/edit/remove items in a string list
// ---------------------------------------------------------------------------
const CHIP_TINTS = [
  "bg-violet-50 text-violet-700 border-violet-100 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900/40",
  "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900/40",
  "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40",
  "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900/40",
];

export function InlineStringList({
  items,
  onSave,
  variant = "bullets",
  placeholder = "Type and press Enter",
  addLabel = "Add",
  testId,
  tinted = false,
  itemClassName = "",
  disabled = false,
}: {
  items: string[];
  onSave: SaveFn<string[]>;
  variant?: "bullets" | "chips" | "quotes" | "steps";
  placeholder?: string;
  addLabel?: string;
  testId: string;
  /** chips only: cycle the persona-style tint palette. */
  tinted?: boolean;
  itemClassName?: string;
  disabled?: boolean;
}) {
  const propList = Array.isArray(items) ? items.filter((s) => typeof s === "string") : [];
  const [failedList, setFailedList] = useState<string[] | null>(null);
  const list = failedList ?? propList;
  // editIndex: index being edited; list.length = the "add new" row.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (editIndex !== null) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editIndex]);

  const doSave = async (next: string[], failureValue: string[] = next): Promise<boolean> => {
    setSaving(true);
    try {
      await onSave(next);
      setFailedList(null);
      return true;
    } catch {
      setFailedList(failureValue);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (i: number) => {
    if (saving || disabled) return;
    resolvedRef.current = false;
    setDraft(i < list.length ? list[i] : "");
    setEditIndex(i);
  };

  const commit = async (keepAdding = false) => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    const i = editIndex;
    setEditIndex(null);
    if (i === null) return;
    const next = draft.trim();
    if (i >= list.length) {
      // Add-new row
      if (!next) return;
      const saved = await doSave([...list, next]);
      if (saved && keepAdding) {
        startEdit(list.length + 1);
      }
    } else if (!next) {
      await doSave(list.filter((_, idx) => idx !== i), list);
    } else if (next !== list[i]) {
      await doSave(list.map((s, idx) => (idx === i ? next : s)));
    } else if (failedList) {
      // The displayed value is an unsaved optimistic draft. Enter without
      // changing it is an explicit retry, not a no-op against server props.
      await doSave(list);
    }
  };

  const cancel = () => {
    resolvedRef.current = true;
    setEditIndex(null);
    setFailedList(null);
  };

  const remove = (i: number) => {
    if (saving || disabled) return;
    void doSave(list.filter((_, idx) => idx !== i), list);
  };

  const editInput = (widthClass: string) => (
    <input
      ref={inputRef}
      value={draft}
      maxLength={500}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void commit(editIndex !== null && editIndex >= list.length);
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      placeholder={placeholder}
      className={`bg-background border border-input rounded px-1.5 py-0.5 text-[13px] outline-none focus:ring-1 focus:ring-ring ${widthClass}`}
      data-testid={`input-${testId}-item`}
    />
  );

  const removeBtn = (i: number) =>
    !disabled && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          remove(i);
        }}
        className="shrink-0 rounded-full p-0.5 text-muted-foreground/50 hover:text-destructive opacity-0 group-hover/li:opacity-100 transition-opacity"
        title="Remove"
        data-testid={`button-remove-${testId}-${i}`}
      >
        <X className="h-3 w-3" />
      </button>
    );

  const addRow = !disabled && (
    editIndex === list.length ? (
      <div className={variant === "chips" ? "inline-flex" : "mt-1.5"}>{editInput(variant === "chips" ? "w-40" : "w-full")}</div>
    ) : (
      <button
        type="button"
        onClick={() => startEdit(list.length)}
        className={`inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground/60 hover:text-foreground transition-colors ${
          variant === "chips"
            ? "rounded-full border border-dashed border-border px-2.5 py-1"
            : "mt-1.5"
        }`}
        data-testid={`button-add-${testId}`}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        {addLabel}
      </button>
    )
  );

  if (variant === "chips") {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`list-${testId}`}>
        {list.map((s, i) =>
          editIndex === i ? (
            <span key={i} className="inline-flex">{editInput("w-40")}</span>
          ) : (
            <span
              key={i}
              className={`group/li inline-flex items-center gap-0.5 text-[11px] rounded-full px-2.5 py-1 border whitespace-normal break-words max-w-full text-left leading-snug ${
                disabled ? "" : "cursor-text"
              } ${tinted ? CHIP_TINTS[i % CHIP_TINTS.length] : "bg-muted/40 border-border/60"} ${itemClassName}`}
              onClick={() => startEdit(i)}
              data-testid={`chip-${testId}-${i}`}
            >
              {s}
              {removeBtn(i)}
            </span>
          ),
        )}
        {addRow}
      </div>
    );
  }

  return (
    <div data-testid={`list-${testId}`}>
      <ul className="space-y-1.5">
        {list.map((s, i) => (
          <li key={i} className="group/li flex gap-2 items-start text-[13px] leading-snug" data-testid={`item-${testId}-${i}`}>
            {variant === "steps" ? (
              <span className="mt-0.5 h-5 w-5 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center flex-shrink-0 text-muted-foreground">{i + 1}</span>
            ) : variant === "quotes" ? (
              <Quote className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0 mt-0.5" />
            ) : (
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
            )}
            {editIndex === i ? (
              editInput("w-full")
            ) : (
              <span
                className={`min-w-0 flex-1 rounded-sm ${disabled ? "" : "cursor-text hover:bg-muted/40 transition-colors"} ${
                  variant === "quotes" ? "italic text-foreground/70" : ""
                } ${itemClassName}`}
                onClick={() => startEdit(i)}
              >
                {variant === "quotes" ? `"${s}"` : s}
              </span>
            )}
            {removeBtn(i)}
          </li>
        ))}
      </ul>
      {addRow}
    </div>
  );
}
